"use client";

import { useRef } from "react";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import Section from "@/components/common/Section/Section";
import { TextElementMen } from "@/components/ui-kit/TextElementMen/TextElementMen";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import colorTheme from "@/styles/theme.mjs";
import { cn } from "@/utils/general";
import { segmentEvent } from "@/utils/segmentfunctions";

import styles from "./Growth.module.scss";

interface TimelineItemData {
  title: string;
  description: string;
  descriptionClasses?: string;
}

interface GrowthData {
  title: string;
  timeline: TimelineItemData[];
  disclaimer: string;
}

interface GrowthProps {
  data: GrowthData;
  theme?: string;
}

const MOBILE_HEADER_HEIGHT = 96;

const DESKTOP_SCROLL_START = "36% bottom";
const DESKTOP_SCROLL_END = "bottom 38%";
const DESKTOP_INTRO_SCRUB_DURATION = 0.18;

const Growth = ({ data, theme }: GrowthProps) => {
  const { title, timeline, disclaimer } = data;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const mobileProgressRef = useRef<HTMLDivElement>(null);
  const desktopProgressRef = useRef<HTMLDivElement>(null);
  const tagsRef = useRef<(HTMLDivElement | null)[]>([]);
  const copyRef = useRef<(HTMLDivElement | null)[]>([]);

  const isMen = theme === "men";

  useGSAP(() => {
    const mm = gsap.matchMedia();

    // Mobile media query setup for GSAP animations
    mm.add("(max-width: 767px) and (min-height: 600px)", () => {
      ScrollTrigger.create({
        trigger: wrapperRef.current,
        scrub: 1,
        pin: "#growth-pin-wrapper",
        start: `top-=${MOBILE_HEADER_HEIGHT} top`,
        end: "+=500",
        invalidateOnRefresh: true,
        refreshPriority: 2,
      });

      const scrollTl = gsap.timeline({
        scrollTrigger: {
          trigger: wrapperRef.current,
          scrub: 1,
          start: "30% bottom",
          end: "+=1000",
          invalidateOnRefresh: true,
          refreshPriority: 1,
        },
      });

      // Get reference to the timeline's ScrollTrigger and refresh it after pin applies
      const timelineSt = scrollTl.scrollTrigger;
      if (timelineSt) {
        // Use a small delay to ensure pin has fully applied before refreshing timeline markers
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            timelineSt.refresh();
          });
        });
      }

      /* Progress fill uses scaleY (height is fixed in CSS) to avoid layout thrash / CLS */
      const mobileScaleAt = (targetHeight: number) => {
        const track = mobileProgressRef.current?.parentElement;
        if (!track) return 0;
        const maxH = track.offsetHeight - 40;
        return maxH > 0 ? Math.min(1, Math.max(0, targetHeight / maxH)) : 0;
      };

      gsap.set(mobileProgressRef.current, {
        scaleY: 0,
        transformOrigin: "top center",
      });

      scrollTl
        .to(
          tagsRef.current[0],
          {
            backgroundColor: colorTheme.color.nova.brand.accent["80"],
            duration: 0.15,
          },
          "step-1"
        )
        .to(
          copyRef.current[0],
          {
            opacity: 1,
            duration: 0.15,
          },
          "step-1"
        )
        .add(() =>
          segmentEvent("Interactive Section Viewed", {
            module_location: "mid_fold",
            slide_number: 1,
            slide_title: "Month 1-3",
          })
        )
        .to(mobileProgressRef.current, {
          scaleY: () => {
            const h = mobileProgressRef.current?.parentElement?.offsetHeight ?? 0;
            return mobileScaleAt(h * 0.5 - 50);
          },
        })
        .to(
          tagsRef.current[1],
          {
            backgroundColor: colorTheme.color.nova.brand.accent["80"],
            duration: 0.15,
          },
          "step-2"
        )
        .to(
          copyRef.current[1],
          {
            opacity: 1,
            duration: 0.15,
          },
          "step-2"
        )
        .add(() =>
          segmentEvent("Interactive Section Viewed", {
            module_location: "mid_fold",
            slide_number: 2,
            slide_title: "Month 4-6",
          })
        )
        .to(mobileProgressRef.current, {
          scaleY: () => {
            const h = mobileProgressRef.current?.parentElement?.offsetHeight ?? 0;
            return mobileScaleAt(h - 100);
          },
        })
        .to(
          tagsRef.current[2],
          {
            backgroundColor: colorTheme.color.nova.brand.accent["80"],
            duration: 0.15,
          },
          "step-3"
        )
        .to(
          copyRef.current[2],
          {
            opacity: 1,
            duration: 0.15,
          },
          "step-3"
        )
        .add(() =>
          segmentEvent("Interactive Section Viewed", {
            module_location: "mid_fold",
            slide_number: 3,
            slide_title: "Month 7+",
          })
        )
        .to(mobileProgressRef.current, { scaleY: 1 });
    });

    // Desktop media query setup for GSAP animations
    mm.add("(min-width: 768px)", () => {
      gsap.set(desktopProgressRef.current, {
        scaleX: 0,
        transformOrigin: "left center",
      });

      ScrollTrigger.create({
        trigger: wrapperRef.current,
        scrub: 1,
        pin: "#growth-pin-wrapper",
        start: DESKTOP_SCROLL_START,
        end: DESKTOP_SCROLL_END,
        invalidateOnRefresh: true,
        refreshPriority: 1,
      });

      const scrollTl = gsap.timeline({
        scrollTrigger: {
          trigger: wrapperRef.current,
          scrub: 1,
          start: DESKTOP_SCROLL_START,
          end: DESKTOP_SCROLL_END,
          invalidateOnRefresh: true,
          refreshPriority: 1,
        },
      });

      const timelineSt = scrollTl.scrollTrigger;
      if (timelineSt) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            timelineSt.refresh();
          });
        });
      }

      /* Row layout: scaleX milestones align with 1/3 column centers; step labels group each tag + copy pair when the line reaches that milestone */
      scrollTl
        .to({}, { duration: DESKTOP_INTRO_SCRUB_DURATION })
        .to(desktopProgressRef.current, { scaleX: 0.17, duration: 0.2 })
        .to(
          tagsRef.current[0],
          {
            backgroundColor: colorTheme.color.nova.brand.accent["80"],
            duration: 0.2,
          },
          "step-1"
        )
        .to(
          copyRef.current[0],
          {
            opacity: 1,
            duration: 0.2,
          },
          "step-1"
        )
        .add(() =>
          segmentEvent("Interactive Section Viewed", {
            module_location: "mid_fold",
            slide_number: 1,
            slide_title: "Month 1-3",
          })
        )
        .to(desktopProgressRef.current, { scaleX: 0.5, duration: 0.22 })
        .to(
          tagsRef.current[1],
          {
            backgroundColor: colorTheme.color.nova.brand.accent["80"],
            duration: 0.15,
          },
          "step-2"
        )
        .to(
          copyRef.current[1],
          {
            opacity: 1,
            duration: 0.15,
          },
          "step-2"
        )
        .add(() =>
          segmentEvent("Interactive Section Viewed", {
            module_location: "mid_fold",
            slide_number: 2,
            slide_title: "Month 4-6",
          })
        )
        .to(desktopProgressRef.current, { scaleX: 0.85, duration: 0.22 })
        .to(
          tagsRef.current[2],
          {
            backgroundColor: colorTheme.color.nova.brand.accent["80"],
            duration: 0.15,
          },
          "step-3"
        )
        .to(
          copyRef.current[2],
          {
            opacity: 1,
            duration: 0.15,
          },
          "step-3"
        )
        .add(() =>
          segmentEvent("Interactive Section Viewed", {
            module_location: "mid_fold",
            slide_number: 3,
            slide_title: "Month 7+",
          })
        )
        .to(desktopProgressRef.current, { scaleX: 1, duration: 0.12 });
    });
  }, []);

  return (
    <div ref={wrapperRef} className={cn(styles.wrapper)}>
      <Section className={cn("section-wrapper", styles.sectionWrapper)}>
        <div className="mb-8 md:mb-10 lg:mb-20 text-center">
          {isMen ? (
            <TextElementMen text={title} className="Title-30-Super-Normal uppercase" element="h2" />
          ) : (
            <TextElementNova text={title} className="Subtitle-20-Dia-Normal" element="h2" />
          )}
        </div>
        <div
          className={cn(
            "mb-8 md:mb-10 lg:mb-20  flex flex-col md:flex-row relative md:gap-10 flex-1 justify-between md:justify-normal max-w-[1120px] mx-auto"
          )}
        >
          <div className={cn(styles.progressBar, isMen ? "men" : "master")}>
            <div ref={mobileProgressRef} className={styles.mobileProgress} />
            <div ref={desktopProgressRef} className={styles.desktopProgress} />
          </div>
          {timeline.map((item, index) => (
            <div
              key={index}
              className={cn(
                "w-full mb-8 last:mb-0 md:mb-0  md:w-1/3 text-center",
                styles.timelineItem
              )}
            >
              <div
                className={cn("tag", styles.tag, "mb-0 md:mb-6")}
                ref={(el) => {
                  tagsRef.current[index] = el;
                }}
              >
                <TextElementNova
                  element={`h3`}
                  text={item.title}
                  className="Caption-10-Dia-Uppercase tag-text leading-[16px]"
                />
              </div>
              <div className={styles.copyWrapper}>
                <div
                  ref={(el) => {
                    copyRef.current[index] = el;
                  }}
                  className={cn("copy-wrapper-ref opacity-60")}
                >
                  <TextElementNova
                    text={item.description}
                    className={cn(
                      "copy Body-20-Dia-Normal text-nova-base-black",
                      item?.descriptionClasses || ""
                    )}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div>
          {isMen ? (
            <TextElementMen
              text={disclaimer}
              className="Caption-10-Dia-Mono text-nova-transparency-black-60 text-center"
            />
          ) : (
            <TextElementNova
              text={disclaimer}
              className="Caption-10-Dia-Normal text-nova-transparency-black-60 text-center"
            />
          )}
        </div>
      </Section>
    </div>
  );
};

export default Growth;
